import { useEffect, useMemo, useState } from "react";
import { Check, X, Trash2, Eye, Heart, Instagram, ExternalLink, Pencil, FileDown, Video } from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function EditModal({ video, onClose, onSaved }) {
  const [form, setForm] = useState({
    artist_name: video.artist_name || "",
    title: video.title || "",
    description: video.description || "",
    instagram: video.instagram || "",
    whatsapp: video.whatsapp || "",
    instagram_source_url: video.instagram_source_url || "",
    views: video.views ?? 0,
    likes: video.likes ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

      // 1) Update metadata
      const meta = {
        artist_name: form.artist_name,
        title: form.title,
        description: form.description,
        instagram: form.instagram,
        whatsapp: form.whatsapp,
        instagram_source_url: form.instagram_source_url,
      };
      const r = await fetch(`${API}/api/admin/video-submissions/${video.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(meta),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || "Erro ao salvar metadados");
        setSaving(false);
        return;
      }

      // 2) Update stats (views + likes) only when changed
      const statsChanged =
        Number(form.views) !== Number(video.views || 0) ||
        Number(form.likes) !== Number(video.likes || 0);
      if (statsChanged) {
        const sr = await fetch(`${API}/api/admin/video-submissions/${video.id}/stats`, {
          method: "PUT",
          headers,
          body: JSON.stringify({ views: Number(form.views) || 0, likes: Number(form.likes) || 0 }),
        });
        if (!sr.ok) {
          const d = await sr.json().catch(() => ({}));
          toast.error(d.detail || "Erro ao salvar views/curtidas");
          setSaving(false);
          return;
        }
      }

      toast.success("Vídeo atualizado!");
      onSaved();
      onClose();
    } catch {
      toast.error("Falha de rede");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur" data-testid="edit-modal">
      <form
        onSubmit={save}
        className="bg-[#0F0F0F] border border-white/10 rounded-2xl max-w-lg w-full p-6 space-y-3"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-xl text-white">Editar Vídeo</h3>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-edit">
            <X size={20} />
          </button>
        </div>

        <div>
          <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
            Nome do artista *
          </label>
          <input
            required
            value={form.artist_name}
            onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
            className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
            data-testid="edit-artist-name"
          />
        </div>
        <div>
          <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
            Título *
          </label>
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
            data-testid="edit-title"
          />
        </div>
        <div>
          <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
            Descrição
          </label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none resize-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
              Instagram
            </label>
            <input
              value={form.instagram}
              onChange={(e) => setForm({ ...form, instagram: e.target.value })}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
              placeholder="@artista"
            />
          </div>
          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
              WhatsApp
            </label>
            <input
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1">
            Link original do vídeo
          </label>
          <input
            type="url"
            value={form.instagram_source_url}
            onChange={(e) => setForm({ ...form, instagram_source_url: e.target.value })}
            className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none"
            placeholder="https://instagram.com/p/... ou https://youtube.com/..."
            data-testid="edit-source-url"
          />
          <p className="text-[10px] text-zinc-600 mt-1">URL onde o vídeo foi publicado originalmente (Instagram, YouTube, TikTok, etc).</p>
        </div>

        {/* Estatísticas editáveis */}
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1 flex items-center gap-1">
              <Eye size={12} /> Views (visualizações)
            </label>
            <input
              type="number"
              min="0"
              value={form.views}
              onChange={(e) => setForm({ ...form, views: e.target.value })}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none tabular-nums"
              data-testid="edit-views"
            />
          </div>
          <div>
            <label className="block text-xs uppercase font-bold tracking-wider text-zinc-500 mb-1 flex items-center gap-1">
              <Heart size={12} /> Curtidas
            </label>
            <input
              type="number"
              min="0"
              value={form.likes}
              onChange={(e) => setForm({ ...form, likes: e.target.value })}
              className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#FF007F] focus:outline-none tabular-nums"
              data-testid="edit-likes"
            />
          </div>
        </div>

        <div className="flex gap-2 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-white/5 hover:bg-white/10 text-white py-3 rounded-lg font-bold"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-[#FF007F] hover:bg-[#FF3399] text-white py-3 rounded-lg font-bold disabled:opacity-50"
            data-testid="save-edit"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const totals = useMemo(() => {
    let views = 0;
    let likes = 0;
    for (const v of videos) {
      views += Number(v.views) || 0;
      likes += Number(v.likes) || 0;
    }
    return { views, likes };
  }, [videos]);

  const exportCsv = () => {
    if (!videos.length) return;
    const rows = [...videos]
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .map((v, i) => ({
        posicao: i + 1,
        artista: v.artist_name || "",
        titulo: v.title || "",
        status: v.status || "",
        views: v.views || 0,
        curtidas: v.likes || 0,
        url_video: v.video_url || "",
        url_original: v.instagram_source_url || "",
        instagram: v.instagram || "",
        created_at: v.created_at || "",
      }));
    const header = Object.keys(rows[0]);
    const escape = (val) => {
      const s = String(val ?? "");
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header.join(";"), ...rows.map((r) => header.map((h) => escape(r[h])).join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vitrine-videos-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success(`Planilha exportada (${rows.length} vídeos)`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/api/admin/video-submissions${filter ? `?status=${filter}` : ""}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      setVideos(Array.isArray(d) ? d : []);
    } catch {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter]);

  const setStatus = async (id, status) => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/video-submissions/${id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        toast.success(status === "approved" ? "Aprovado!" : "Rejeitado");
        load();
      }
    } catch {
      toast.error("Erro");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este vídeo? Não tem como desfazer.")) return;
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/video-submissions/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        toast.success("Excluído");
        load();
      }
    } catch {
      toast.error("Erro");
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl">
        <h1 className="text-3xl sm:text-4xl font-black mb-2">Vitrine — Vídeos</h1>
        <p className="text-zinc-500 text-sm mb-6">Modere as submissões de vídeos dos artistas. Edite views/curtidas no botão Editar.</p>

        {/* Totais agregados */}
        {videos.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid="videos-total-card">
              <Video size={20} className="text-violet-400" />
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Vídeos</p>
                <p className="text-lg font-black text-white tabular-nums">{videos.length}</p>
              </div>
            </div>
            <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid="videos-views-card">
              <Eye size={20} className="text-cyan-400" />
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Views</p>
                <p className="text-lg font-black text-white tabular-nums">{totals.views.toLocaleString("pt-BR")}</p>
              </div>
            </div>
            <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-3 flex items-center gap-3" data-testid="videos-likes-card">
              <Heart size={20} className="text-[#FF007F]" />
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider">Curtidas</p>
                <p className="text-lg font-black text-white tabular-nums">{totals.likes.toLocaleString("pt-BR")}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 flex-wrap items-center">
          {[
            { v: "", l: "Todos" },
            { v: "pending", l: "Pendentes" },
            { v: "approved", l: "Aprovados" },
            { v: "rejected", l: "Rejeitados" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition ${
                filter === f.v ? "bg-[#FF007F] text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"
              }`}
              data-testid={`filter-${f.v || "all"}`}
            >
              {f.l}
            </button>
          ))}

          <button
            onClick={exportCsv}
            disabled={videos.length === 0}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black rounded-full text-sm font-bold transition"
            data-testid="export-videos-csv"
            title="Baixar planilha CSV de views e curtidas"
          >
            <FileDown size={14} /> Planilha CSV
          </button>
        </div>

        {loading ? (
          <p className="text-zinc-500">Carregando...</p>
        ) : videos.length === 0 ? (
          <p className="text-zinc-500">Nenhum vídeo.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((v) => (
              <div
                key={v.id}
                className="bg-[#0F0F0F] border border-white/10 rounded-2xl overflow-hidden"
                data-testid={`admin-video-${v.id}`}
              >
                <div className="aspect-[9/16] bg-zinc-900">
                  <video
                    src={v.video_url?.startsWith("http") ? v.video_url : `${API}${v.video_url}`}
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                        v.status === "approved"
                          ? "bg-emerald-500/20 text-emerald-300"
                          : v.status === "rejected"
                          ? "bg-red-500/20 text-red-300"
                          : "bg-yellow-500/20 text-yellow-300"
                      }`}
                    >
                      {v.status}
                    </span>
                    {v.source === "instagram" && (
                      <span className="text-[10px] bg-pink-500/20 text-pink-300 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                        <Instagram size={10} /> Instagram
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-white text-sm truncate">{v.title}</h3>
                  <p className="text-xs text-[#FF007F] font-semibold uppercase truncate">{v.artist_name}</p>
                  <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                    <span className="flex items-center gap-1"><Eye size={11} /> {v.views || 0}</span>
                    <span className="flex items-center gap-1"><Heart size={11} /> {v.likes || 0}</span>
                  </div>
                  {v.instagram_source_url && (
                    <a
                      href={v.instagram_source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-pink-300 hover:text-pink-400 flex items-center gap-1 mt-1 truncate"
                    >
                      <ExternalLink size={10} /> URL original
                    </a>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setEditing(v)}
                      className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1"
                      title="Editar"
                      data-testid={`edit-${v.id}`}
                    >
                      <Pencil size={14} /> Editar
                    </button>
                    {v.status !== "approved" && (
                      <button
                        onClick={() => setStatus(v.id, "approved")}
                        className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1"
                        data-testid={`approve-${v.id}`}
                      >
                        <Check size={14} /> Aprovar
                      </button>
                    )}
                    {v.status !== "rejected" && (
                      <button
                        onClick={() => setStatus(v.id, "rejected")}
                        className="flex-1 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1"
                        data-testid={`reject-${v.id}`}
                      >
                        <X size={14} /> Rejeitar
                      </button>
                    )}
                    <button
                      onClick={() => del(v.id)}
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-300 px-3 py-2 rounded-lg"
                      title="Excluir"
                      data-testid={`delete-${v.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <EditModal video={editing} onClose={() => setEditing(null)} onSaved={load} />
        )}
      </div>
    </AdminLayout>
  );
}
